import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  // PurchaseRequest
  SearchPurchaseRequestClient,
  GetPurchaseRequestClient,
  DeletePurchaseRequestClient,
  UpdatePurchaseRequestClient,
  UpdateEvaluationResultClient,
  CreatePurchaseRequestClient,
  ArchivingPurchaseRequestClient,
  GetPurchaseRequestWithAllSubInfoClient,
  // PurchaseProposal
  DeletePurchaseProposalClient,
  CreatePurchaseProposalClient,
  ApprovePurchaseProposalClient,
  // PurchaseOrder
  SearchPurchaseOrderClient,
  GetPurchaseOrderClient,
  DeletePurchaseOrderClient,
  UpdatePurchaseOrderClient,
  UpdatePurchaseOrderStatusClient,
  CreatePurchaseOrderClient,
  ArchivingPurchaseOrderClient,
  // PurchaseProposalCompany
  SearchProposalCompanyClient,
  GetProposalCompanyClient,
  UpdateProposalCompanyClient,
  CreateProposalCompanyClient,
  // PurchaseProposalRequest
  CreatePurchaseProposalRequestClient,
  GetPurchaseProposalRequestClient,
  UpdatePurchaseProposalRequestClient,
  SearchPurchaseProposalRequestClient,

  // PurchaseProposalRequestSendMail
  GetPurchaseProposalRequestSendMailClient,
  GetPurchaseProposalRequestSubInfoClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
// PurchaseRequest
const searchPurchaseRequestClient = new SearchPurchaseRequestClient(SERVICE_URL)
const getPurchaseRequestClient = new GetPurchaseRequestClient(SERVICE_URL)
const createPurchaseRequestClient = new CreatePurchaseRequestClient(SERVICE_URL)
const deletePurchaseRequestClient = new DeletePurchaseRequestClient(SERVICE_URL)
const updatePurchaseRequestClient = new UpdatePurchaseRequestClient(SERVICE_URL)
const updateEvaluationResultClient = new UpdateEvaluationResultClient(SERVICE_URL)
const archivingPurchaseRequestClient = new ArchivingPurchaseRequestClient(SERVICE_URL)
const getPurchaseRequestWithAllSubInfoClient = new GetPurchaseRequestWithAllSubInfoClient(
  SERVICE_URL
)

// PurchaseProposal
const createPurchaseProposalClient = new CreatePurchaseProposalClient(SERVICE_URL)
const deletePurchaseProposalClient = new DeletePurchaseProposalClient(SERVICE_URL)
const approvePurchaseProposalClient = new ApprovePurchaseProposalClient(SERVICE_URL)

// PurchaseOrder
const searchPurchaseOrderClient = new SearchPurchaseOrderClient(SERVICE_URL)
const getPurchaseOrderClient = new GetPurchaseOrderClient(SERVICE_URL)
const createPurchaseOrderClient = new CreatePurchaseOrderClient(SERVICE_URL)
const deletePurchaseOrderClient = new DeletePurchaseOrderClient(SERVICE_URL)
const updatePurchaseOrderClient = new UpdatePurchaseOrderClient(SERVICE_URL)
const updatePurchaseOrderStatusClient = new UpdatePurchaseOrderStatusClient(SERVICE_URL)
const archivingPurchaseOrderClient = new ArchivingPurchaseOrderClient(SERVICE_URL)

// PurchaseProposalCompany
const searchProposalCompanyClient = new SearchProposalCompanyClient(SERVICE_URL)
const getProposalCompanyClient = new GetProposalCompanyClient(SERVICE_URL)
const updateProposalCompanyClient = new UpdateProposalCompanyClient(SERVICE_URL)
const createProposalCompanyClient = new CreateProposalCompanyClient(SERVICE_URL)

// PurchaseProposalRequest
const createPurchaseProposalRequestClient = new CreatePurchaseProposalRequestClient(SERVICE_URL)
const getPurchaseProposalRequestClient = new GetPurchaseProposalRequestClient(SERVICE_URL)
const updatePurchaseProposalRequestClient = new UpdatePurchaseProposalRequestClient(SERVICE_URL)
const searchPurchaseProposalRequestClient = new SearchPurchaseProposalRequestClient(SERVICE_URL)

// PurchaseProposalRequestSendMail
const getPurchaseProposalRequestSendMailClient = new GetPurchaseProposalRequestSendMailClient(
  SERVICE_URL
)
const getPurchaseProposalRequestSubInfoClient = new GetPurchaseProposalRequestSubInfoClient(
  SERVICE_URL
)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  isAdded: false,
  excel: null,
  purchasePdf: null,
  purchaseProposalRequest: null,
  getCompany: null,
  postCompany: null,
  request: null,
  requestProposal: null,
  detail: null,
  order: null,
  proposalCompany: null,
  mail: null,
  subInfo: null,
  isUpdateEvaluationResult: false,
  isUpdateStatus: false,
}

// Async Thunks
// PurchaseRequest
export const purchaseRequestGetAll = createAsyncThunk(
  'purchase/requestGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseRequestClient.search(
        params.name,
        params.purchaseRequestStatusId,
        params.projectId,
        params.companyId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setRequest(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestExcel = createAsyncThunk(
  'purchase/requestExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseRequestClient.excel(
        params.name,
        params.purchaseRequestStatusId,
        params.projectId,
        params.companyId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestGetId = createAsyncThunk(
  'purchase/requestGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseRequestClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestDelete = createAsyncThunk(
  'purchase/requestDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deletePurchaseRequestClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestArchiving = createAsyncThunk(
  'purchase/requestArchiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingPurchaseRequestClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestCreate = createAsyncThunk(
  'purchase/requestCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createPurchaseRequestClient.create(
        params.Name,
        params.ProjectId,
        params.CompanyId,
        params.EditDate,
        params.DueDate,
        params.PurchaseRequestStatusId,
        params.Note,
        params.RelatedUserId,
        params.File,
        params.PurchaseRequestItems
      )
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestUpdate = createAsyncThunk(
  'purchase/requestUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePurchaseRequestClient.update(
        params.Id,
        params.Name,
        params.ProjectId,
        params.CompanyId,
        params.EditDate,
        params.DueDate,
        params.PurchaseRequestStatusId,
        params.Note,
        params.RelatedUserId,
        params.File,
        params.PurchaseRequestItems
      )
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestUpdateEvaluationResult = createAsyncThunk(
  'purchase/requestUpdateEvaluationResult',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateEvaluationResultClient.update(params)
      dispatch(setUpdateEvaluationResult(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseRequestGetAllWithAllSubInfo = createAsyncThunk(
  'purchase/requestGetAllWithAllSubInfo',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseRequestWithAllSubInfoClient.get(params)
      dispatch(setPurchasePdf(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// PurchaseProposal
export const purchaseProposalDelete = createAsyncThunk(
  'purchase/proposalDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deletePurchaseProposalClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalCreate = createAsyncThunk(
  'purchase/proposalCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))

      const response = await createPurchaseProposalClient.create(
        params.PurchaseRequestId,
        params.Name,
        params.ProposalCompanyId,
        params.TotalPrice,
        params.TotalPriceWithTax,
        params.Transportation,
        params.PaymentDetail,
        params.DeliveryTime,
        params.Brand,
        params.ProposalFile,
        params.PurchaseProposalStatusId,
        params.LaborTotalPrice,
        params.ProductTotalPrice,
        params.TSEFile
      )
      dispatch(setAdded(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalApprove = createAsyncThunk(
  'purchase/proposalApprove',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await approvePurchaseProposalClient.update(params)
      dispatch(setRedirectPage(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// PurchaseOrder
export const purchaseOrderGetAll = createAsyncThunk(
  'purchase/orderGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseOrderClient.search(
        params.name,
        params.companyId,
        params.purchaseOrderStatusId,
        params.projectId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setOrder(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderExcel = createAsyncThunk(
  'purchase/orderExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseOrderClient.excel(
        params.name,
        params.companyId,
        params.purchaseOrderStatusId,
        params.projectId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderGetId = createAsyncThunk(
  'purchase/orderGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseOrderClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderDelete = createAsyncThunk(
  'purchase/orderDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deletePurchaseOrderClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderArchiving = createAsyncThunk(
  'purchase/orderArchiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingPurchaseOrderClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderCreate = createAsyncThunk(
  'purchase/orderCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createPurchaseOrderClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderUpdate = createAsyncThunk(
  'purchase/orderUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePurchaseOrderClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseOrderUpdateStatus = createAsyncThunk(
  'purchase/orderUpdateStatus',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePurchaseOrderStatusClient.updateStatus(params)
      dispatch(setUpdateStatus(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// PurchaseProposalCompany
export const purchaseProposalCompanyGetAll = createAsyncThunk(
  'purchase/proposalCompanyGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchProposalCompanyClient.search(
        params.name,
        params.categoryId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setProposalCompany(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalCompanyExcel = createAsyncThunk(
  'purchase/proposalCompanyExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchProposalCompanyClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalCompanyGetId = createAsyncThunk(
  'purchase/proposalCompanyGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getProposalCompanyClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalCompanyCreate = createAsyncThunk(
  'purchase/proposalCompanyCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createProposalCompanyClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalCompanyUpdate = createAsyncThunk(
  'purchase/proposalCompanyUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateProposalCompanyClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// PurchaseProposalRequest
export const purchaseProposalRequestCreate = createAsyncThunk(
  'purchase/proposalRequestCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createPurchaseProposalRequestClient.create(params)
      dispatch(setPurchaseProposalRequest(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalRequestGetAll = createAsyncThunk(
  'purchase/proposalRequestGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseProposalRequestClient.get(params)
      dispatch(setGetCompany(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalRequestUpdate = createAsyncThunk(
  'purchase/proposalRequestUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setRedirect(false))
      dispatch(setLoading(true))
      const response = await updatePurchaseProposalRequestClient.update(
        params.PurchaseProposalGuid,
        params.Note,
        params.File,
        params.LaborTotalPrice,
        params.ProductTotalPrice,
        params.TotalPrice,
        params.TotalPriceWithTax,
        params.Transportation,
        params.PaymentDetail,
        params.DeliveryTime,
        params.Brand,
        params.TSEFile
      )
      dispatch(setPostCompany(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalRequestGetAllSearch = createAsyncThunk(
  'purchase/proposalRequestGetAllSearch',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseProposalRequestClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setRequestProposal(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalRequestExcel = createAsyncThunk(
  'purchase/proposalRequestExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPurchaseProposalRequestClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// PurchaseProposalRequestSendMail
export const purchaseProposalRequestSendMail = createAsyncThunk(
  'purchase/proposalRequestSendMail',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseProposalRequestSendMailClient.get(params)
      dispatch(setMail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const purchaseProposalRequestSubInfo = createAsyncThunk(
  'purchase/proposalRequestSubInfo',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPurchaseProposalRequestSubInfoClient.get(params)
      dispatch(setSubInfo(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)
// Slice
const purchaseSlice = createSlice({
  name: 'purchase',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setRequest: (state, action) => {
      state.request = action.payload
      state.loading = false
    },
    setRequestProposal: (state, action) => {
      state.requestProposal = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setUpdateEvaluationResult: (state, action) => {
      state.isUpdateEvaluationResult = action.payload
      state.loading = false
    },
    setUpdateStatus: (state, action) => {
      state.isUpdateStatus = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setAdded: (state, action) => {
      state.isAdded = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setPurchasePdf: (state, action) => {
      state.purchasePdf = action.payload
      state.loading = false
    },
    setPurchaseProposalRequest: (state, action) => {
      state.purchaseProposalRequest = action.payload
      state.loading = false
    },
    setGetCompany: (state, action) => {
      state.getCompany = action.payload
      state.loading = false
    },
    setPostCompany: (state, action) => {
      state.postCompany = action.payload
      state.loading = false
    },
    setOrder: (state, action) => {
      state.order = action.payload
      state.loading = false
    },
    setProposalCompany: (state, action) => {
      state.proposalCompany = action.payload
      state.loading = false
    },
    setMail: (state, action) => {
      state.mail = action.payload
      state.loading = false
    },
    setSubInfo: (state, action) => {
      state.subInfo = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('purchase/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setRequest,
  setRequestProposal,
  setDetail,
  setDeleted,
  setRedirectPage,
  setUpdateEvaluationResult,
  setUpdateStatus,
  setRedirect,
  setArchived,
  setAdded,
  setExcel,
  setPurchasePdf,
  setPurchaseProposalRequest,
  setGetCompany,
  setPostCompany,
  setOrder,
  setProposalCompany,
  setMail,
  setSubInfo,
  resetState,
} = purchaseSlice.actions

// Selectors
export const selectPurchaseLoading = (state) => state.purchase.loading
export const selectPurchaseError = (state) => state.purchase.error
export const selectPurchaseRequest = (state) => state.purchase.request
export const selectPurchaseRequestProposal = (state) => state.purchase.requestProposal
export const selectPurchaseDetail = (state) => state.purchase.detail
export const selectPurchaseDeleted = (state) => state.purchase.isDeleted
export const selectPurchaseUpdateEvaluationResult = (state) => state.purchase.isUpdateEvaluationResult
export const selectPurchaseUpdateStatus = (state) => state.purchase.isUpdateStatus
export const selectPurchaseRedirectPage = (state) => state.purchase.isRedirectPage
export const selectPurchaseRedirect = (state) => state.purchase.isRedirect
export const selectPurchaseArchived = (state) => state.purchase.isArchived
export const selectPurchaseAdded = (state) => state.purchase.isAdded
export const selectPurchaseExcel = (state) => state.purchase.excel
export const selectPurchasePdf = (state) => state.purchase.purchasePdf
export const selectPurchaseProposalRequest = (state) => state.purchase.purchaseProposalRequest
export const selectPurchaseGetCompany = (state) => state.purchase.getCompany
export const selectPurchasePostCompany = (state) => state.purchase.postCompany
export const selectPurchaseOrder = (state) => state.purchase.order
export const selectPurchaseProposalCompany = (state) => state.purchase.proposalCompany
export const selectPurchaseMail = (state) => state.purchase.mail
export const selectPurchaseSubInfo = (state) => state.purchase.subInfo

export default purchaseSlice.reducer
